/**
 * API Service for handling HTTP requests
 */
export class ApiService {
  constructor(basePath, middleware = {}) {
    this.basePath = basePath;
    this.middleware = middleware;
  }

  // Generic request for all endpoints
  async httpRequest(method, path, body, options = {}) {
    let response;
    try {
      response = await fetch(`${this.basePath}${path}`, {
        method,
        credentials: "include",
        headers: {
          ...(options.multiPart ? {} : body ? { "Content-Type": "application/json" } : {}),
          "Accept-Encoding": "br"
        },
        body: options.multiPart ? body : body ? JSON.stringify(body) : undefined
      });
    } catch (e) {
      if (e instanceof Error) {
        this.middleware.onError?.(e);
        throw e;
      }
    }

    if (response && response.ok) {
      try {
        if (options?.responseType === "blob") {
          return await response.blob();
        } else {
          return await response.json();
        }
      } catch (e) {
        if (e instanceof Error) {
          this.middleware.onError?.(e);
          return e;
        }
      }
    } else {
      // A network-level failure can leave `response` undefined (the catch
      // above only rethrows for `instanceof Error`). Reading .status here
      // then threw a TypeError, which surfaced as the generic frontend
      // "Something went wrong. Please try again." instead of the real fault.
      if (!response) {
        const netError = new Error("Network request failed — no response received");
        netError.status = 0;
        this.middleware.onError?.(netError);
        throw netError;
      }

      // Refresh token on 401.
      //
      // `authAttempt` callers (login) are excluded deliberately. A wrong
      // password returns 401, and because a stale `user_session` flag plus a
      // still-valid 7-day refresh cookie are both normally present the day
      // after a login, this block used to fire on every failed login: it
      // refreshed the token successfully and then REPLAYED the bad-password
      // POST. The second 401 went on to be handled as a session expiry,
      // logging the operator out and redirecting mid-toast — so a simple
      // typo looked like a system error. A failed credential check must
      // never trigger a token refresh.
      if (
        response.status === 401 &&
        localStorage.getItem("user_session") &&
        !options.refreshAttempt &&
        !options.authAttempt
      ) {
        try {
          const refreshResponse = await this.middleware.refreshToken?.();
          if (refreshResponse && refreshResponse.success) {
            // Preserve the original options so `silent` / `responseType`
            // survive the replay.
            return this.httpRequest(method, path, body, { ...options, refreshAttempt: true });
          }
        } catch (error) {
          console.log(error);
        }
      }

      await this.handleResponseError(response, options.silent, options);
    }
  }

  async handleResponseError(response, silent = false, options = {}) {
    let json;
    try {
      json = await response.json();
    } catch (e) {
      json = e;
    }
    // Dump every error response to the console so we can debug from
    // DevTools without needing server access. URL + status + the full
    // body (including `detail` when the backend surfaces a 500-cause).
    try {
      // eslint-disable-next-line no-console
      console.error(
        `[API ${response.status}] ${response.url}`,
        json,
      );
    } catch { /* no-op */ }

    // Mark credential-check failures so the global handler treats them as a
    // normal form error rather than as an expired session (no logout, no
    // redirect). Without this flag a 401 is indistinguishable from a real
    // session expiry.
    if (options.authAttempt && json && typeof json === "object") {
      json.isAuthAttempt = true;
    }

    // Guarantee the thrown object carries a usable message. When the body is
    // not JSON (gateway 502/504, HTML error page) `json` is the parse error,
    // and callers that read `error.message` fell through to the generic
    // frontend fallback.
    if (json && typeof json === "object" && !json.message) {
      json.message =
        response.status === 401
          ? "User ID / Password is not correct"
          : `Request failed (HTTP ${response.status}). See the browser console for details.`;
    }

    // `silent` callers (e.g. type-as-you-search lookups that 404 on every
    // partial term) suppress the global error toast — the caller handles
    // the empty/failed result locally. The error is still logged + thrown.
    if (!silent) {
      this.middleware.onError?.(json);
    }
    throw json;
  }

  // Generic GET request
  getRequest(apiUrl) {
    return this.httpRequest("GET", `${apiUrl}`, null);
  }

  // ==================== Auth APIs ====================
  // authAttempt: a 401 here means "bad credentials", NOT "session expired".
  // It must not trigger a token refresh or a logout/redirect.
  getLoginUser(data) {
    return this.httpRequest("POST", "/generic/login", data, { authAttempt: true });
  }

  // Forgot password (OTP) — public endpoints, mirror the POS flow.
  forgotPassword(user_phone_number) {
    return this.httpRequest("POST", "/generic/forgot-password", { user_phone_number });
  }
  validateForgotPasswordOtp(user_phone_number, otp) {
    return this.httpRequest("POST", "/generic/validate-otp", { user_phone_number, otp });
  }
  resetPasswordAfterOtp(shortId, password, confirm_password) {
    return this.httpRequest("POST", `/generic/reset-password/${encodeURIComponent(shortId)}`, { password, confirm_password });
  }

  getLogoutUser() {
    return this.httpRequest("GET", "/generic/logout");
  }

  updateAccessToken() {
    return this.httpRequest("GET", "/generic/refresh-token");
  }

  getAppConfig() {
    return this.httpRequest("GET", "/generic/get-app-config");
  }

  getMyDepots() {
    return this.httpRequest("GET", "/generic/my-depots");
  }

  switchDepot(depot_code) {
    return this.httpRequest("POST", "/generic/switch-depot", { depot_code });
  }

  // ==================== Lookup APIs (no module guard) ====================
  lookupRoles() {
    return this.httpRequest("GET", "/generic/lookup-roles");
  }

  getDepotStockSummary() {
    return this.httpRequest("GET", "/generic/depot-stock-summary");
  }

  lookupDepots(searchTerm = "") {
    let url = "/generic/lookup-depots";
    if (searchTerm) url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.httpRequest("GET", url);
  }
  lookupStates() {
    return this.httpRequest("GET", "/generic/lookup-states");
  }
  lookupProducts(searchTerm, page = 0, pageSize = 20) {
    return this.httpRequest("GET", `/generic/lookup-products?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`);
  }

  // ==================== Users APIs ====================
  getAllUsers(page = 0, pageSize = 10, searchTerm = "") {
    let url = `/users/get-all-users/${page}/${pageSize}`;
    if (searchTerm) url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.httpRequest("GET", url);
  }

  getSingleUser(userId) {
    return this.httpRequest("GET", `/users/get-single-user/${userId}`);
  }

  addUser(data) {
    return this.httpRequest("POST", "/users/add-user", data);
  }

  updateUser(userId, data) {
    return this.httpRequest("PUT", `/users/update-user/${userId}`, data);
  }

  deleteUser(userId) {
    return this.httpRequest("DELETE", `/users/delete-user/${userId}`);
  }

  // ==================== Roles APIs ====================
  getAllRoles(page = 0, pageSize = 10, searchTerm = "") {
    let url = `/roles/get-all-roles/${page}/${pageSize}`;
    if (searchTerm) url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.httpRequest("GET", url);
  }

  // ==================== Audit Trail APIs (admin-only) ====================
  getAuditLogs(page = 0, pageSize = 25, filters = {}) {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);
    if (filters.q) params.append("q", filters.q);
    if (filters.table_name) params.append("table_name", filters.table_name);
    if (filters.action) params.append("action", filters.action);
    if (filters.changed_by) params.append("changed_by", filters.changed_by);
    if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.append("dateTo", filters.dateTo);
    return this.httpRequest("GET", `/audit-logs?${params.toString()}`);
  }
  getAuditTables() {
    return this.httpRequest("GET", "/audit-logs/tables");
  }

  getSingleRole(roleId) {
    return this.httpRequest("GET", `/roles/get-single-role/${roleId}`);
  }

  addRole(data) {
    return this.httpRequest("POST", "/roles/add-role", data);
  }

  updateRole(roleId, data) {
    return this.httpRequest("PUT", `/roles/update-role/${roleId}`, data);
  }

  deleteRole(roleId) {
    return this.httpRequest("DELETE", `/roles/delete-role/${roleId}`);
  }

  getAllModules() {
    return this.httpRequest("GET", "/roles/get-all-modules");
  }

  // ==================== Dashboard APIs ====================
  getDashboardStats() {
    return this.httpRequest("GET", "/dashboard/stats");
  }

  getOpenSalesOrders(page = 0, pageSize = 10, searchTerm = "") {
    const qs = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
    return this.httpRequest("GET", `/dashboard/open-sales-orders/${page}/${pageSize}${qs}`);
  }

  getDashboardChartData() {
    return this.httpRequest("GET", "/dashboard/chart-data");
  }

  // ==================== Invoice APIs ====================
  createInvoiceFromSO(data) {
    return this.httpRequest("POST", "/invoices/create-from-so", data);
  }

  // SO soft-lock — open-time concurrency UX. CreateInvoiceFromSO
  // acquires on mount, heartbeats while editing, releases on unmount.
  acquireSoLock(so_id) {
    return this.httpRequest("POST", "/invoices/so-lock/acquire", { so_id });
  }
  heartbeatSoLock(so_id) {
    return this.httpRequest("POST", "/invoices/so-lock/heartbeat", { so_id });
  }
  releaseSoLock(so_id) {
    return this.httpRequest("POST", "/invoices/so-lock/release", { so_id });
  }

  // Depo Return soft-lock — same shape as the SO lock above. Wired on
  // CreateEditDepoReturn while a draft is open for edit/confirm.
  acquireDepoReturnLock(return_id) {
    return this.httpRequest("POST", "/depo-returns/locks/acquire", { return_id });
  }
  heartbeatDepoReturnLock(return_id) {
    return this.httpRequest("POST", "/depo-returns/locks/heartbeat", { return_id });
  }
  releaseDepoReturnLock(return_id) {
    return this.httpRequest("POST", "/depo-returns/locks/release", { return_id });
  }

  // Source-invoice lock for the "create return with reference" flow —
  // mirrors the SO lock on Create Invoice. Two users returning the same
  // invoice see a blocking screen instead of both succeeding.
  acquireReturnInvoiceLock(invoice_id) {
    return this.httpRequest("POST", "/depo-returns/invoice-locks/acquire", { invoice_id });
  }
  heartbeatReturnInvoiceLock(invoice_id) {
    return this.httpRequest("POST", "/depo-returns/invoice-locks/heartbeat", { invoice_id });
  }
  releaseReturnInvoiceLock(invoice_id) {
    return this.httpRequest("POST", "/depo-returns/invoice-locks/release", { invoice_id });
  }

  createDirectInvoice(data) {
    return this.httpRequest("POST", "/invoices/create-direct", data);
  }

  updateInvoice(invoiceNo, data) {
    return this.httpRequest("PUT", `/invoices/update/${invoiceNo}`, data);
  }

  confirmInvoice(invoiceNo) {
    return this.httpRequest("PATCH", `/invoices/confirm/${invoiceNo}`);
  }
  // Drafts have NULL invoice_no — confirm by invoice_id instead.
  confirmDraftInvoice(invoiceId) {
    return this.httpRequest("PATCH", `/invoices/confirm-draft/${invoiceId}`);
  }
  // Update a draft (no invoice_no yet) by invoice_id.
  updateDraftInvoice(invoiceId, payload) {
    return this.httpRequest("PUT", `/invoices/update-draft/${invoiceId}`, payload);
  }
  // Fetch a draft (no invoice_no yet) by its invoice_id. Backend's
  // existing get-by-no endpoint can't find drafts, so add a sibling.
  getInvoiceById(invoiceId) {
    return this.httpRequest("GET", `/invoices/get-by-id/${invoiceId}`);
  }

  getInvoice(invoiceNo) {
    return this.httpRequest("GET", `/invoices/get/${invoiceNo}`);
  }

  getAllInvoices(page = 0, pageSize = 10, filters = {}) {
    let url = `/invoices/get-all/${page}/${pageSize}`;
    const params = new URLSearchParams();
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.status) params.append("status", filters.status);
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    return this.httpRequest("GET", url);
  }

  searchProducts(searchTerm, page = 0, pageSize = 20, storeType = '', storeCode = '') {
    let url = `/invoices/search-products?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (storeType) url += `&store_type=${encodeURIComponent(storeType)}`;
    if (storeCode) url += `&store_code=${encodeURIComponent(storeCode)}`;
    return this.httpRequest("GET", url);
  }
  lookupStore(storeCode, options = {}) {
    return this.httpRequest("GET", `/invoices/lookup-store/${encodeURIComponent(storeCode)}`, null, options);
  }
  scanBarcode(barcode, storeType = '', storeCode = '') {
    let url = `/invoices/scan-barcode/${encodeURIComponent(barcode)}`;
    const params = [];
    if (storeType) params.push(`store_type=${encodeURIComponent(storeType)}`);
    if (storeCode) params.push(`store_code=${encodeURIComponent(storeCode)}`);
    if (params.length) url += `?${params.join('&')}`;
    return this.httpRequest("GET", url);
  }
  getAllBrands() {
    return this.httpRequest("GET", `/invoices/brands`);
  }
  /**
   * Returns {restricted, mode, brand_ids[], exclude_brand_ids[]} for
   * the given store. Pass at least store_code; store_type is derived
   * server-side from PUC when omitted.
   */
  getBrandFilter(storeCode = "", storeType = "") {
    const params = [];
    if (storeCode) params.push(`store_code=${encodeURIComponent(storeCode)}`);
    if (storeType) params.push(`store_type=${encodeURIComponent(storeType)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return this.httpRequest("GET", `/invoices/brand-filter${qs}`);
  }
  /**
   * Returns the store's live SAP FI balance.
   *   { success, amount, remark, available_balance }
   * Negative amount = credit (positive available_balance);
   * positive amount = debit (negative available_balance).
   */
  getStoreBalance(storeCode) {
    return this.httpRequest(
      "GET",
      `/invoices/store-balance?store_code=${encodeURIComponent(storeCode)}`
    );
  }
  getSODetails(orderId) {
    return this.httpRequest("GET", `/invoices/get-so-details/${orderId}`);
  }
  getDraftInvoiceBySO(soId) {
    return this.httpRequest("GET", `/invoices/draft-by-so?so_id=${encodeURIComponent(soId)}`);
  }

  // ==================== Depots APIs ====================
  getAllDepots(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    let url = `/depots/get-all-depots/${page}/${pageSize}`;
    const params = new URLSearchParams();
    if (searchTerm) params.append("searchTerm", searchTerm);
    if (filterTerm) params.append("filterTerm", JSON.stringify(filterTerm));
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    return this.httpRequest("GET", url);
  }

  getSingleDepot(depotId) {
    return this.httpRequest("GET", `/depots/get-single-depot/${depotId}`);
  }

  addDepot(data) {
    return this.httpRequest("POST", "/depots/add-depot", data);
  }

  updateDepot(depotId, data) {
    return this.httpRequest("PUT", `/depots/update-depot/${depotId}`, data);
  }

  deleteDepot(depotId) {
    return this.httpRequest("DELETE", `/depots/delete-depot/${depotId}`);
  }

  // ==================== Companies APIs ====================
  _buildUrl(base, page, pageSize, searchTerm, filterTerm, sortBy = "", sortDir = "") {
    let url = `${base}/${page}/${pageSize}`;
    const params = new URLSearchParams();
    if (searchTerm) params.append("searchTerm", searchTerm);
    if (filterTerm) params.append("filterTerm", JSON.stringify(filterTerm));
    if (sortBy) params.append("sortBy", sortBy);
    if (sortDir) params.append("sortDir", sortDir);
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    return url;
  }

  getAllCompanies(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/companies/get-all-companies", page, pageSize, searchTerm, filterTerm));
  }
  getSingleCompany(id) { return this.httpRequest("GET", `/companies/get-single-company/${id}`); }
  addCompany(data) { return this.httpRequest("POST", "/companies/add-company", data); }
  updateCompany(id, data) { return this.httpRequest("PUT", `/companies/update-company/${id}`, data); }
  deleteCompany(id) { return this.httpRequest("DELETE", `/companies/delete-company/${id}`); }
  uploadCompanyLogo(id, formData) {
    return this.httpRequest("PUT", `/companies/upload-logo/${id}`, formData, { multiPart: true });
  }

  // ==================== Warehouse Zones APIs ====================
  getAllZones(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/warehouse-zones/get-all-zones", page, pageSize, searchTerm, filterTerm));
  }
  getZonesByDepot(depotId) { return this.httpRequest("GET", `/warehouse-zones/get-zones-by-depot/${depotId}`); }
  getSingleZone(id) { return this.httpRequest("GET", `/warehouse-zones/get-single-zone/${id}`); }
  addZone(data) { return this.httpRequest("POST", "/warehouse-zones/add-zone", data); }
  updateZone(id, data) { return this.httpRequest("PUT", `/warehouse-zones/update-zone/${id}`, data); }
  deleteZone(id) { return this.httpRequest("DELETE", `/warehouse-zones/delete-zone/${id}`); }

  // ==================== Warehouse Bins APIs ====================
  getAllBins(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/warehouse-bins/get-all-bins", page, pageSize, searchTerm, filterTerm));
  }
  getBinsByZone(zoneId) { return this.httpRequest("GET", `/warehouse-bins/get-bins-by-zone/${zoneId}`); }
  getSingleBin(id) { return this.httpRequest("GET", `/warehouse-bins/get-single-bin/${id}`); }
  addBin(data) { return this.httpRequest("POST", "/warehouse-bins/add-bin", data); }
  updateBin(id, data) { return this.httpRequest("PUT", `/warehouse-bins/update-bin/${id}`, data); }
  deleteBin(id) { return this.httpRequest("DELETE", `/warehouse-bins/delete-bin/${id}`); }

  // ==================== Customers APIs ====================
  getAllCustomers(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/customers/get-all-customers", page, pageSize, searchTerm, filterTerm));
  }
  getSingleCustomer(id) { return this.httpRequest("GET", `/customers/get-single-customer/${id}`); }
  addCustomer(data) { return this.httpRequest("POST", "/customers/add-customer", data); }
  updateCustomer(id, data) { return this.httpRequest("PUT", `/customers/update-customer/${id}`, data); }
  deleteCustomer(id) { return this.httpRequest("DELETE", `/customers/delete-customer/${id}`); }

  // ==================== Suppliers APIs ====================
  getAllSuppliers(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/suppliers/get-all-suppliers", page, pageSize, searchTerm, filterTerm));
  }
  getActiveSuppliers() { return this.httpRequest("GET", "/suppliers/get-active-suppliers"); }
  getSingleSupplier(id) { return this.httpRequest("GET", `/suppliers/get-single-supplier/${id}`); }
  addSupplier(data) { return this.httpRequest("POST", "/suppliers/add-supplier", data); }
  updateSupplier(id, data) { return this.httpRequest("PUT", `/suppliers/update-supplier/${id}`, data); }
  deleteSupplier(id) { return this.httpRequest("DELETE", `/suppliers/delete-supplier/${id}`); }


  // ==================== Number Series APIs ====================
  getAllNumberSeries(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/number-series/get-all", page, pageSize, searchTerm, filterTerm));
  }
  getSingleNumberSeries(id) { return this.httpRequest("GET", `/number-series/get-single/${id}`); }
  addNumberSeries(data) { return this.httpRequest("POST", "/number-series/add", data); }
  updateNumberSeries(id, data) { return this.httpRequest("PUT", `/number-series/update/${id}`, data); }
  deleteNumberSeries(id) { return this.httpRequest("DELETE", `/number-series/delete/${id}`); }

  // ==================== Configurations APIs ====================
  getAllConfigs(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/configurations/get-all", page, pageSize, searchTerm, filterTerm));
  }
  getConfigByKey(key) { return this.httpRequest("GET", `/configurations/get-by-key/${key}`); }
  addConfig(data) { return this.httpRequest("POST", "/configurations/add", data); }
  upsertConfigByKey(data) { return this.httpRequest("POST", "/configurations/upsert-by-key", data); }
  updateConfig(id, data) { return this.httpRequest("PUT", `/configurations/update/${id}`, data); }
  deleteConfig(id) { return this.httpRequest("DELETE", `/configurations/delete/${id}`); }

  // ==================== Purchase Orders APIs ====================
  getAllPOs(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/purchase-orders/get-all-purchase-orders", page, pageSize, searchTerm, filterTerm));
  }
  getSinglePO(id) { return this.httpRequest("GET", `/purchase-orders/get-single-purchase-order/${id}`); }
  createPO(data) { return this.httpRequest("POST", "/purchase-orders/add-purchase-order", data); }
  updatePO(id, data) { return this.httpRequest("PUT", `/purchase-orders/update-purchase-order/${id}`, data); }
  approvePO(id) { return this.httpRequest("PUT", `/purchase-orders/approve-purchase-order/${id}`); }
  cancelPO(id) { return this.httpRequest("PUT", `/purchase-orders/cancel-purchase-order/${id}`); }
  deletePO(id) { return this.httpRequest("DELETE", `/purchase-orders/delete-purchase-order/${id}`); }

  // ==================== GRN APIs ====================
  getAllGRNs(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/grn/get-all-grns", page, pageSize, searchTerm, filterTerm));
  }
  getSingleGRN(id) { return this.httpRequest("GET", `/grn/get-single-grn/${id}`); }
  createGRN(data) { return this.httpRequest("POST", "/grn/create-grn", data); }
  completeGRN(id) { return this.httpRequest("PUT", `/grn/complete-grn/${id}`); }
  deleteGRN(id) { return this.httpRequest("DELETE", `/grn/delete-grn/${id}`); }
  // Vendor open POs from SAP for the current depot (always own-depot scoped).
  getSapOpenPOs() { return this.httpRequest("GET", "/grn/sap-open-pos"); }
  // Complete GRN against a vendor PO. { po_number, invoice_no, invoice_date }
  receivePOGrn(data) { return this.httpRequest("POST", "/grn/receive-po", data); }

  // ==================== STO Receipt (depot-to-depot inward) APIs ====================
  lookupStoDelivery(delivery_no) {
    return this.httpRequest("POST", "/sto-receipt/lookup", { delivery_no });
  }
  acceptStoDelivery(delivery_no) {
    return this.httpRequest("POST", "/sto-receipt/accept", { delivery_no });
  }

  // ==================== Batches APIs ====================
  getAllBatches(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/batches/get-all-batches", page, pageSize, searchTerm, filterTerm));
  }
  getSingleBatch(id) { return this.httpRequest("GET", `/batches/get-single-batch/${id}`); }
  updateBatch(id, data) { return this.httpRequest("PUT", `/batches/update-batch/${id}`, data); }

  // ==================== Stock APIs ====================
  getStockOnHand(page = 0, pageSize = 10, searchTerm = "", filterTerm = null, sortBy = "", sortDir = "") {
    return this.httpRequest("GET", this._buildUrl("/stock/get-stock-on-hand", page, pageSize, searchTerm, filterTerm, sortBy, sortDir));
  }
  getStockMovements(page = 0, pageSize = 10, searchTerm = "", filterTerm = null, sortBy = "", sortDir = "") {
    return this.httpRequest("GET", this._buildUrl("/stock/get-stock-movements", page, pageSize, searchTerm, filterTerm, sortBy, sortDir));
  }
  adjustStock(data) { return this.httpRequest("POST", "/stock/adjust-stock", data); }
  getStockRegister(page = 0, pageSize = 50, searchTerm = "", asOnDate = "", sortBy = "", sortDir = "") {
    let url = `/stock/get-stock-register/${page}/${pageSize}`;
    const params = [];
    if (searchTerm) params.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    if (asOnDate) params.push(`as_on_date=${encodeURIComponent(asOnDate)}`);
    if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
    if (sortDir) params.push(`sortDir=${encodeURIComponent(sortDir)}`);
    if (params.length) url += `?${params.join("&")}`;
    return this.httpRequest("GET", url);
  }
  getInwardDetails(productSku) {
    return this.httpRequest("GET", `/stock/get-inward-details/${encodeURIComponent(productSku)}`);
  }
  getOutwardDetails(productSku) {
    return this.httpRequest("GET", `/stock/get-outward-details/${encodeURIComponent(productSku)}`);
  }
  getReturnInDetails(productSku) {
    return this.httpRequest("GET", `/stock/get-return-in-details/${encodeURIComponent(productSku)}`);
  }
  // POS-side store balances for a batch of SKUs — used by the
  // walk-in-no-ref return flow to cap qty against what the store
  // actually holds in stock.
  getStoreStockBalances(storeCode, sapCodes = []) {
    const skuParam = encodeURIComponent(sapCodes.join(","));
    return this.httpRequest(
      "GET",
      `/depo-returns/store-stock?store_code=${encodeURIComponent(storeCode)}&sap_codes=${skuParam}`
    );
  }
  getSalesRegister(page = 0, pageSize = 50, searchTerm = "", startDate = "", endDate = "", sortBy = "", sortDir = "") {
    let url = `/stock/get-sales-register/${page}/${pageSize}`;
    const params = [];
    if (searchTerm) params.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
    if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
    if (sortDir) params.push(`sortDir=${encodeURIComponent(sortDir)}`);
    if (params.length) url += `?${params.join("&")}`;
    return this.httpRequest("GET", url);
  }
  getSalesRegisterItems(invoiceId, invoiceDate) {
    return this.httpRequest("GET", `/stock/get-sales-register-items/${invoiceId}/${encodeURIComponent(invoiceDate)}`);
  }
  getPurchaseRegister(page = 0, pageSize = 50, searchTerm = "", startDate = "", endDate = "", sortBy = "", sortDir = "") {
    let url = `/stock/get-purchase-register/${page}/${pageSize}`;
    const params = [];
    if (searchTerm) params.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
    if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
    if (sortDir) params.push(`sortDir=${encodeURIComponent(sortDir)}`);
    if (params.length) url += `?${params.join("&")}`;
    return this.httpRequest("GET", url);
  }
  getPurchaseRegisterItems(grnId) {
    return this.httpRequest("GET", `/stock/get-purchase-register-items/${grnId}`);
  }
  getProductWiseSalesReport(page = 0, pageSize = 50, searchTerm = "", startDate = "", endDate = "", sortBy = "", sortDir = "") {
    let url = `/stock/get-product-wise-sales-report/${page}/${pageSize}`;
    const params = [];
    if (searchTerm) params.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
    if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
    if (sortDir) params.push(`sortDir=${encodeURIComponent(sortDir)}`);
    if (params.length) url += `?${params.join("&")}`;
    return this.httpRequest("GET", url);
  }
  getProductWiseSalesInvoices(sapCode, startDate = "", endDate = "") {
    let url = `/stock/get-product-wise-sales-invoices/${encodeURIComponent(sapCode)}`;
    const params = [];
    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
    if (params.length) url += `?${params.join("&")}`;
    return this.httpRequest("GET", url);
  }

  // ==================== Sales Orders APIs ====================
  getAllSalesOrders(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/sales-orders/get-all-sales-orders", page, pageSize, searchTerm, filterTerm));
  }
  getSingleSalesOrder(id) { return this.httpRequest("GET", `/sales-orders/get-single-sales-order/${id}`); }
  createSalesOrder(data) { return this.httpRequest("POST", "/sales-orders/add-sales-order", data); }
  updateSalesOrder(id, data) { return this.httpRequest("PUT", `/sales-orders/update-sales-order/${id}`, data); }
  approveSalesOrder(id) { return this.httpRequest("PUT", `/sales-orders/approve-sales-order/${id}`); }
  allocateStock(id) { return this.httpRequest("PUT", `/sales-orders/allocate-sales-order/${id}`); }
  cancelSalesOrder(id) { return this.httpRequest("PUT", `/sales-orders/cancel-sales-order/${id}`); }
  deleteSalesOrder(id) { return this.httpRequest("DELETE", `/sales-orders/delete-sales-order/${id}`); }
  getActiveCustomers() { return this.httpRequest("GET", "/customers/get-active-customers"); }

  // ==================== Picking APIs ====================
  getAllPickLists(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/picking/get-all-pick-lists", page, pageSize, searchTerm, filterTerm));
  }
  getSinglePickList(id) { return this.httpRequest("GET", `/picking/get-single-pick-list/${id}`); }
  createPickList(data) { return this.httpRequest("POST", "/picking/create-pick-list", data); }
  assignPickList(id, data) { return this.httpRequest("PUT", `/picking/assign-pick-list/${id}`, data); }
  startPicking(id) { return this.httpRequest("PUT", `/picking/start-picking/${id}`); }
  updatePickedQty(id, data) { return this.httpRequest("PUT", `/picking/update-picked-qty/${id}`, data); }
  completePicking(id) { return this.httpRequest("PUT", `/picking/complete-pick-list/${id}`); }

  // ==================== Dispatch APIs ====================
  getAllDispatches(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/dispatch/get-all-dispatches", page, pageSize, searchTerm, filterTerm));
  }
  getSingleDispatch(id) { return this.httpRequest("GET", `/dispatch/get-single-dispatch/${id}`); }
  createDispatch(data) { return this.httpRequest("POST", "/dispatch/create-dispatch", data); }
  markDispatched(id) { return this.httpRequest("PUT", `/dispatch/mark-dispatched/${id}`); }
  markDelivered(id, data) { return this.httpRequest("PUT", `/dispatch/mark-delivered/${id}`, data); }
  deleteDispatch(id) { return this.httpRequest("DELETE", `/dispatch/delete-dispatch/${id}`); }

  // ==================== WMS Invoice APIs ====================
  getAllWmsInvoices(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/wms-invoices/get-all-invoices", page, pageSize, searchTerm, filterTerm));
  }
  getSingleWmsInvoice(id) { return this.httpRequest("GET", `/wms-invoices/get-single-invoice/${id}`); }
  generateWmsInvoice(soId) { return this.httpRequest("POST", `/wms-invoices/generate-invoice/${soId}`); }

  // ==================== Credit Notes APIs ====================
  getAllCreditNotes(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/credit-notes/get-all-credit-notes", page, pageSize, searchTerm, filterTerm));
  }
  getSingleCreditNote(id) { return this.httpRequest("GET", `/credit-notes/get-single-credit-note/${id}`); }
  createCreditNote(data) { return this.httpRequest("POST", "/credit-notes/add-credit-note", data); }
  approveCreditNote(id) { return this.httpRequest("PUT", `/credit-notes/update-credit-note/${id}`, { cn_status: "confirmed" }); }
  confirmCreditNote(id) { return this.httpRequest("PUT", `/credit-notes/confirm-credit-note/${id}`); }
  cancelCreditNote(id) { return this.httpRequest("PUT", `/credit-notes/cancel-credit-note/${id}`); }
  deleteCreditNote(id) { return this.httpRequest("DELETE", `/credit-notes/delete-credit-note/${id}`); }

  // ==================== Debit Notes APIs ====================
  getAllDebitNotes(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/debit-notes/get-all-debit-notes", page, pageSize, searchTerm, filterTerm));
  }
  getSingleDebitNote(id) { return this.httpRequest("GET", `/debit-notes/get-single-debit-note/${id}`); }
  createDebitNote(data) { return this.httpRequest("POST", "/debit-notes/add-debit-note", data); }
  approveDebitNote(id) { return this.httpRequest("PUT", `/debit-notes/update-debit-note/${id}`, { dn_status: "confirmed" }); }
  confirmDebitNote(id) { return this.httpRequest("PUT", `/debit-notes/confirm-debit-note/${id}`); }
  cancelDebitNote(id) { return this.httpRequest("PUT", `/debit-notes/cancel-debit-note/${id}`); }
  deleteDebitNote(id) { return this.httpRequest("DELETE", `/debit-notes/delete-debit-note/${id}`); }

  // ==================== Payments APIs ====================
  getAllPayments(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/payments/get-all-payments", page, pageSize, searchTerm, filterTerm));
  }
  getSinglePayment(id) { return this.httpRequest("GET", `/payments/get-single-payment/${id}`); }
  createPayment(data) { return this.httpRequest("POST", "/payments/add-payment", data); }
  confirmPayment(id) { return this.httpRequest("PUT", `/payments/update-payment/${id}`, { payment_status: "confirmed" }); }
  bouncePayment(id) { return this.httpRequest("PUT", `/payments/update-payment/${id}`, { payment_status: "bounced" }); }
  cancelPayment(id) { return this.httpRequest("PUT", `/payments/update-payment/${id}`, { payment_status: "cancelled" }); }
  deletePayment(id) { return this.httpRequest("DELETE", `/payments/delete-payment/${id}`); }

  // ==================== Sales Returns APIs ====================
  getAllSalesReturns(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/sales-returns/get-all-returns", page, pageSize, searchTerm, filterTerm));
  }
  getSingleSalesReturn(id) { return this.httpRequest("GET", `/sales-returns/get-single-return/${id}`); }
  createSalesReturn(data) { return this.httpRequest("POST", "/sales-returns/create-return", data); }
  receiveSalesReturn(id) { return this.httpRequest("PUT", `/sales-returns/receive-return/${id}`); }
  completeSalesReturn(id) { return this.httpRequest("PUT", `/sales-returns/complete-return/${id}`); }
  deleteSalesReturn(id) { return this.httpRequest("DELETE", `/sales-returns/delete-sales-return/${id}`); }

  // ==================== Putaway APIs ====================
  getAllPutawayTasks(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/putaway/get-all-tasks", page, pageSize, searchTerm, filterTerm));
  }
  getSinglePutawayTask(id) { return this.httpRequest("GET", `/putaway/get-single-task/${id}`); }
  updatePutawayTask(id, data) { return this.httpRequest("PUT", `/putaway/update-task/${id}`, data); }
  updatePutawayItem(id, data) { return this.httpRequest("PUT", `/putaway/update-item/${id}`, data); }
  completePutaway(id) { return this.httpRequest("PUT", `/putaway/complete-task/${id}`); }

  // ==================== Shelf Transfers APIs ====================
  getAllShelfTransfers(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/shelf-transfers/get-all-transfers", page, pageSize, searchTerm, filterTerm));
  }
  getSingleShelfTransfer(id) { return this.httpRequest("GET", `/shelf-transfers/get-single-transfer/${id}`); }
  createShelfTransfer(data) { return this.httpRequest("POST", "/shelf-transfers/add-transfer", data); }
  approveShelfTransfer(id) { return this.httpRequest("PUT", `/shelf-transfers/approve-transfer/${id}`); }
  completeShelfTransfer(id) { return this.httpRequest("PUT", `/shelf-transfers/complete-transfer/${id}`); }
  deleteShelfTransfer(id) { return this.httpRequest("DELETE", `/shelf-transfers/delete-transfer/${id}`); }

  // ==================== Cycle Counts APIs ====================
  getAllCycleCounts(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/cycle-counts/get-all-counts", page, pageSize, searchTerm, filterTerm));
  }
  getSingleCycleCount(id) { return this.httpRequest("GET", `/cycle-counts/get-single-count/${id}`); }
  createCycleCount(data) { return this.httpRequest("POST", "/cycle-counts/add-count", data); }
  updateCycleCountItem(id, data) { return this.httpRequest("PUT", `/cycle-counts/update-count-item/${id}`, data); }
  approveCycleCount(id) { return this.httpRequest("PUT", `/cycle-counts/approve-count/${id}`); }
  adjustCycleCount(id) { return this.httpRequest("PUT", `/cycle-counts/adjust-count/${id}`); }
  deleteCycleCount(id) { return this.httpRequest("DELETE", `/cycle-counts/delete-count/${id}`); }

  // ==================== Purchase Requests APIs ====================
  getAllPurchaseRequests(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/purchase-requests/get-all-purchase-requests", page, pageSize, searchTerm, filterTerm));
  }
  getSinglePurchaseRequest(id) { return this.httpRequest("GET", `/purchase-requests/get-single-purchase-request/${id}`); }
  getApprovedPRsForDepot() { return this.httpRequest("GET", "/purchase-requests/get-approved-prs-for-depot"); }
  createPurchaseRequest(data) { return this.httpRequest("POST", "/purchase-requests/add-purchase-request", data); }
  updatePurchaseRequest(id, data) { return this.httpRequest("PUT", `/purchase-requests/update-purchase-request/${id}`, data); }
  approvePurchaseRequest(id) { return this.httpRequest("PUT", `/purchase-requests/approve-purchase-request/${id}`); }
  rejectPurchaseRequest(id) { return this.httpRequest("PUT", `/purchase-requests/reject-purchase-request/${id}`); }
  deletePurchaseRequest(id) { return this.httpRequest("DELETE", `/purchase-requests/delete-purchase-request/${id}`); }
  createPOFromPRs(data) { return this.httpRequest("POST", "/purchase-orders/create-po-from-prs", data); }
  getDepotPRMappings() { return this.httpRequest("GET", "/purchase-requests/depot-mappings"); }
  getDepotPRTargets() { return this.httpRequest("GET", "/purchase-requests/depot-targets"); }
  setDepotPRMappings(data) { return this.httpRequest("POST", "/purchase-requests/set-depot-mappings", data); }
  deleteDepotPRMapping(id) { return this.httpRequest("DELETE", `/purchase-requests/delete-depot-mapping/${id}`); }
  searchPRProducts(searchTerm, prType, page = 0, pageSize = 20) {
    let url = `/purchase-requests/search-products?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (prType) url += `&pr_type=${encodeURIComponent(prType)}`;
    return this.httpRequest("GET", url);
  }
  getProductsByPRType(prType, searchTerm = "") {
    let url = `/purchase-requests/products-by-type?pr_type=${encodeURIComponent(prType)}`;
    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.httpRequest("GET", url);
  }

  // ==================== Inter-Depot PO APIs ====================
  getIncomingPRs() { return this.httpRequest("GET", "/purchase-orders/incoming-prs"); }
  getInterDepotPOs(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/purchase-orders/inter-depot", page, pageSize, searchTerm, filterTerm));
  }
  getSingleInterDepotPO(id) { return this.httpRequest("GET", `/purchase-orders/inter-depot-po/${id}`); }
  createInterDepotPO(data) { return this.httpRequest("POST", "/purchase-orders/create-inter-depot-po", data); }
  updateInterDepotPO(id, data) { return this.httpRequest("PUT", `/purchase-orders/update-inter-depot-po/${id}`, data); }

  // ==================== Inter-Depot Invoice APIs ====================
  createInvoiceFromPO(data) { return this.httpRequest("POST", "/invoices/create-from-po", data); }
  confirmInterDepotInvoice(invoiceNo) { return this.httpRequest("PATCH", `/invoices/confirm-inter-depot/${invoiceNo}`); }
  getInterDepotInvoiceByPO(poId) { return this.httpRequest("GET", `/invoices/inter-depot-by-po/${poId}`); }

  // ==================== SAP Logs APIs (super-admin only) ====================
  searchSAPLogs(q = "", page = 0, pageSize = 50, referenceType = "") {
    const params = [];
    if (q) params.push(`q=${encodeURIComponent(q)}`);
    if (referenceType) params.push(`reference_type=${encodeURIComponent(referenceType)}`);
    params.push(`page=${page}`);
    params.push(`pageSize=${pageSize}`);
    return this.httpRequest("GET", `/sap-logs/search?${params.join("&")}`);
  }
  getSAPLog(logId) { return this.httpRequest("GET", `/sap-logs/${logId}`); }

  // ==================== Stock Transfers APIs ====================
  getAllStockTransfers(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/stock-transfers/get-all-transfers", page, pageSize, searchTerm, filterTerm));
  }
  getSingleStockTransfer(id) { return this.httpRequest("GET", `/stock-transfers/get-single-transfer/${id}`); }
  createStockTransfer(data) { return this.httpRequest("POST", "/stock-transfers/add-transfer", data); }
  updateStockTransfer(id, data) { return this.httpRequest("PUT", `/stock-transfers/update-transfer/${id}`, data); }
  approveStockTransfer(id) { return this.httpRequest("PUT", `/stock-transfers/approve-transfer/${id}`); }
  dispatchStockTransfer(id) { return this.httpRequest("PUT", `/stock-transfers/dispatch-transfer/${id}`); }
  receiveStockTransfer(id, data) { return this.httpRequest("PUT", `/stock-transfers/receive-transfer/${id}`, data); }
  deleteStockTransfer(id) { return this.httpRequest("DELETE", `/stock-transfers/delete-transfer/${id}`); }

  // ==================== Stock Levels APIs ====================
  getStockLevels(page = 0, pageSize = 10, searchTerm = "", filterTerm = null, sortBy = "", sortDir = "") {
    return this.httpRequest("GET", this._buildUrl("/stock-levels/get-stock-levels", page, pageSize, searchTerm, filterTerm, sortBy, sortDir));
  }
  updateStockLevel(data) { return this.httpRequest("PUT", "/stock-levels/update-stock-level", data); }
  getStockAlerts() { return this.httpRequest("GET", "/stock-levels/get-stock-alerts"); }

  // ==================== Depo Returns APIs (Wave 1: walk-in) ====================
  getAllDepoReturns(page = 0, pageSize = 10, searchTerm = "", filterTerm = null) {
    return this.httpRequest("GET", this._buildUrl("/depo-returns/list", page, pageSize, searchTerm, filterTerm));
  }
  getSingleDepoReturn(id) {
    return this.httpRequest("GET", `/depo-returns/${id}`);
  }
  lookupReturnInvoice({ invoice_no, sap_invoice_no }) {
    const params = new URLSearchParams();
    if (invoice_no) params.append("invoice_no", invoice_no);
    if (sap_invoice_no) params.append("sap_invoice_no", sap_invoice_no);
    return this.httpRequest("GET", `/depo-returns/invoice-lookup?${params.toString()}`);
  }
  createDepoReturnDraft(data) {
    return this.httpRequest("POST", "/depo-returns/create-draft", data);
  }
  updateDepoReturnDraft(id, data) {
    return this.httpRequest("PUT", `/depo-returns/update/${id}`, data);
  }
  confirmDepoReturn(id) {
    return this.httpRequest("PATCH", `/depo-returns/confirm/${id}`);
  }
  deleteDepoReturn(id) {
    return this.httpRequest("DELETE", `/depo-returns/${id}`);
  }
  // Wave 2 — POS-originated returns
  posListOpenReturnSOs(page = 0, pageSize = 10, searchTerm = "") {
    const params = new URLSearchParams();
    if (searchTerm) params.append("searchTerm", searchTerm);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.httpRequest("GET", `/depo-returns/pos/open/${page}/${pageSize}${qs}`);
  }
  posGetReturnSO(return_order_id) {
    return this.httpRequest("GET", `/depo-returns/pos/${return_order_id}`);
  }
  createDepoReturnFromPos(data) {
    return this.httpRequest("POST", "/depo-returns/create-from-pos", data);
  }

  // ==================== Opening Stock APIs ====================
  getOpeningStock(page = 0, pageSize = 50, searchTerm = "", fy_code = "", depot_code = "") {
    const params = new URLSearchParams();
    if (searchTerm) params.append("searchTerm", searchTerm);
    if (fy_code) params.append("fy_code", fy_code);
    if (depot_code) params.append("depot_code", depot_code);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.httpRequest("GET", `/opening-stock/get-opening-stock/${page}/${pageSize}${qs}`);
  }
  upsertOpeningStock(data) { return this.httpRequest("POST", "/opening-stock/upsert", data); }
  bulkUploadOpeningStock(data) { return this.httpRequest("POST", "/opening-stock/bulk-upload", data); }
  deleteOpeningStock(id) { return this.httpRequest("DELETE", `/opening-stock/delete/${id}`); }
  carryForwardStock(data) { return this.httpRequest("POST", "/opening-stock/carry-forward", data); }
  loadOpeningStockToSOH(data) { return this.httpRequest("POST", "/opening-stock/load-to-stock", data); }

  // ==================== Expiry Management APIs ====================
  getExpiryDashboard() { return this.httpRequest("GET", "/expiry-management/get-expiry-dashboard"); }
  blockExpiredStock() { return this.httpRequest("PUT", "/expiry-management/block-expired-stock"); }
  getExpirySettings() { return this.httpRequest("GET", "/expiry-management/get-expiry-settings"); }
  updateExpirySettings(data) { return this.httpRequest("PUT", "/expiry-management/update-expiry-settings", data); }

  // ==================== Depot Retail Sale Bill APIs ====================
  // Buyer lookups
  getAssociateBuyer(id) {
    return this.httpRequest("GET", `/sale-bills/buyers/associate-buyer/${encodeURIComponent(id)}`);
  }
  getAssociateBuyerByMobile(mobile) {
    return this.httpRequest("GET", `/sale-bills/buyers/associate-buyer-by-mobile/${encodeURIComponent(mobile)}`);
  }
  getDisplayWallBuyer(id) {
    return this.httpRequest("GET", `/sale-bills/buyers/display-wall/${encodeURIComponent(id)}`);
  }
  getRegisteredBuyer(mobile) {
    return this.httpRequest("GET", `/sale-bills/buyers/registered-buyer/${encodeURIComponent(mobile)}`);
  }

  // Product search (depot-stock-aware)
  searchSaleBillProducts(searchTerm = "", page = 0, pageSize = 20) {
    const url =
      `/sale-bills/products/search?searchTerm=${encodeURIComponent(searchTerm)}` +
      `&page=${page}&pageSize=${pageSize}`;
    return this.httpRequest("GET", url);
  }

  // OTP generators (server logs the OTP — no SMS yet)
  generateWalletOtp(data) {
    return this.httpRequest("POST", "/sale-bills/wallet/generate-otp", data);
  }
  generateRbOtp(data) {
    return this.httpRequest("POST", "/sale-bills/rb-bill/generate-otp", data);
  }
  generateAbOtp(data) {
    return this.httpRequest("POST", "/sale-bills/ab-bill/generate-otp", data);
  }

  // Bill CRUD
  createSaleBill(data) {
    return this.httpRequest("POST", "/sale-bills", data);
  }
  getAllSaleBills(page = 0, pageSize = 10, filters = {}) {
    const params = new URLSearchParams({page, pageSize});
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.status) params.append("status", filters.status);
    return this.httpRequest("GET", `/sale-bills?${params.toString()}`);
  }
  getSaleBillByNo(invoiceNo) {
    return this.httpRequest("GET", `/sale-bills/${encodeURIComponent(invoiceNo)}`);
  }

  // Sale Return
  createSaleReturn(data) {
    return this.httpRequest("POST", "/sale-bills/return", data);
  }
  getAllSaleReturns(page = 0, pageSize = 10, filters = {}) {
    const params = new URLSearchParams({page, pageSize});
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    return this.httpRequest("GET", `/sale-bills/returns?${params.toString()}`);
  }
  getSaleReturnByNo(invoiceNo) {
    return this.httpRequest("GET", `/sale-bills/return/${encodeURIComponent(invoiceNo)}`);
  }

  // ==================== POS-mirror aliases ====================
  // These methods exist so the verbatim-copied POS SalesBill code in
  // pages/SaleBills/pos-mirror/ can call apiService using POS method
  // names without modification. Each alias forwards to the matching
  // depo method, adapting request shapes where the contracts differ.
  async getAllProduct(data) {
    // POS preloads ALL eligible products for a buyer; depo does
    // search-on-demand. Pre-fetch a generous slice (200 rows) so the
    // POS UI has something to filter client-side.
    //
    // Field-shape adapter: POS-mirror code reads `balance` (for stock)
    // and `purchase_volume` (for PV); depo's product rows use
    // `available_stock` and don't ship a PV field. Without this remap
    // the cart's +/- qty buttons silently no-op because the
    // `quantity <= record.stock_balance` check compares against
    // undefined.
    const res = await this.searchSaleBillProducts("", 0, 200);
    if (res?.data && Array.isArray(res.data)) {
      res.data = res.data.map((p) => ({
        ...p,
        balance: p.balance ?? p.available_stock ?? 0,
        purchase_volume: p.purchase_volume ?? 0,
      }));
    }
    return res;
  }
  getAssociateMobileBuyer(mobile) {
    return this.getAssociateBuyerByMobile(mobile);
  }
  generateSaleBillOTP(data) {
    return this.generateWalletOtp(data);
  }
  generateRegisteredBuyerOTP(data) {
    return this.generateRbOtp(data);
  }
  generateABdBuyerOTP(data) {
    return this.generateAbOtp(data);
  }
  updateSaleBill(data) {
    // POS uses "update" naming for what's actually creating a new bill
    // (no edit-existing-bill flow in the SalesBill screen)
    return this.createSaleBill(data);
  }
}
